import { useEffect } from 'react';

const BUTTON_LABEL_MAP = [
  { key: 'lucide-menu', label: 'Open menu' },
  { key: 'lucide-x', label: 'Close' },
  { key: 'lucide-chevron-left', label: 'Previous' },
  { key: 'lucide-chevron-right', label: 'Next' },
  { key: 'lucide-chevron-down', label: 'Expand options' },
  { key: 'lucide-chevron-up', label: 'Collapse options' },
  { key: 'lucide-search', label: 'Search' },
  { key: 'lucide-trash', label: 'Delete item' },
  { key: 'lucide-plus', label: 'Add item' },
];

const isWidgetNode = (el) =>
  (el.className || '').toString().includes('aifnmjmchg-') ||
  (el.className || '').toString().includes('headlessui-');

const inferButtonLabel = (button) => {
  const classBlob = (button.className || '').toString().toLowerCase();
  const iconMatch = BUTTON_LABEL_MAP.find((item) => classBlob.includes(item.key));
  if (iconMatch) return iconMatch.label;

  const iconClass = button.querySelector('[class*="lucide-"]')?.getAttribute('class') || '';
  const iconClassMatch = BUTTON_LABEL_MAP.find((item) => iconClass.toLowerCase().includes(item.key));
  if (iconClassMatch) return iconClassMatch.label;

  if (button.getAttribute('aria-controls')?.toLowerCase().includes('menu')) {
    return button.getAttribute('aria-expanded') === 'true' ? 'Close menu' : 'Open menu';
  }

  return 'Widget action';
};

const patchButton = (button) => {
  if (
    button.hasAttribute('aria-label') ||
    button.hasAttribute('aria-labelledby') ||
    button.hasAttribute('title')
  ) {
    return;
  }

  const text = (button.textContent || '').replace(/\s+/g, ' ').trim();
  if (text) return;
  button.setAttribute('aria-label', inferButtonLabel(button));
};

const patchFormField = (field) => {
  if (field.getAttribute('type') === 'hidden') return;
  if (field.hasAttribute('aria-label') || field.hasAttribute('aria-labelledby') || field.hasAttribute('title')) {
    return;
  }

  const fallback =
    field.getAttribute('placeholder') || field.getAttribute('name') || field.id || 'Widget input';
  field.setAttribute('aria-label', fallback);
};

const patchNode = (root) => {
  if (!(root instanceof Element)) return;

  if (root.matches('img:not([alt])')) {
    root.setAttribute('alt', '');
  }

  if (root.matches('button')) {
    patchButton(root);
  }

  if (root.matches('input, select, textarea')) {
    patchFormField(root);
  }

  if (root.matches('svg[role="img"]') && !root.querySelector('title')) {
    const title = document.createElementNS('http://www.w3.org/2000/svg', 'title');
    title.textContent = 'Illustration';
    root.prepend(title);
  }

  if (isWidgetNode(root)) {
    root.querySelectorAll('button').forEach(patchButton);
    root.querySelectorAll('input, select, textarea').forEach(patchFormField);
    root.querySelectorAll('img:not([alt])').forEach((img) => img.setAttribute('alt', ''));
  }
};

const runInitialPatch = () => {
  document.querySelectorAll('[class*="aifnmjmchg-"], [class*="headlessui-"]').forEach(patchNode);
};

const AccessibilityGuard = () => {
  useEffect(() => {
    if (typeof window === 'undefined') return;

    const idle = window.requestIdleCallback || ((cb) => setTimeout(cb, 16));
    const idleId = idle(runInitialPatch);

    const observer = new MutationObserver((mutations) => {
      for (const mutation of mutations) {
        mutation.addedNodes.forEach((node) => {
          if (node.nodeType !== Node.ELEMENT_NODE) return;
          patchNode(node);
        });
      }
    });

    observer.observe(document.body, {
      childList: true,
      subtree: true,
    });

    return () => {
      observer.disconnect();
      if (window.cancelIdleCallback) {
        window.cancelIdleCallback(idleId);
      } else {
        clearTimeout(idleId);
      }
    };
  }, []);

  return null;
};

export default AccessibilityGuard;
